import React from 'react';
import {
  ModalView,
  Stack,
  Form,
  TextFieldControl,
  SelectControl,
  withTranslation,
} from '@piwikpro/ui-components';
import { FormValidator } from '@piwikpro/form-crate';
import { ConfigService, errorBoundary, withInject } from '@piwikpro/platform';
import { Users } from '@piwikpro/users-crate';
import { connect, ConnectedProps } from 'react-redux';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';

const mapStateToProps = (state: { session: SessionState }) => ({
  invitingUserLanguage: state.session.language,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  users: Users
  validation: FormValidator
  Title: React.ComponentType<any>
  config: ConfigService
}

interface Props extends InjectedProps, ReduxProps {
  abort: () => void
  finish: (id: string) => void
  fetchUsersParams: any
  t: (key: string) => string
}

const InviteUser: React.FC<Props> = ({
  abort,
  finish,
  users,
  fetchUsersParams,
  validation,
  t,
  Title,
  config,
  invitingUserLanguage,
}) => {
  const handleSubmit = async (values, {
    setErrors,
    setSubmitting,
  }) => {
    try {
      const response = await users.inviteUser(values);
      users.fetchUsers(fetchUsersParams);
      finish(response.data.id);
    } catch (err: any) {
      setErrors(err.errors);
      setSubmitting(false);
    }
  };

  return (
    <Title part={t('users.invite.title')} subset>
      <Form
        id="invite-user-form"
        initialValues={{
          email: '',
          language: invitingUserLanguage?.split('-')[0],
        }}
        validationSchema={validation.createValidationSchema(validators => ({
          email: validators.users.email,
        }))}
        onSubmit={handleSubmit}
      >
        <ModalView
          title={t('users.invite.title')}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Form.Cancel onClick={abort} />
              <Form.Submit text={t('users.invite.send-invitation-btn')} />
            </Stack>
          )}
        >
          <Stack vertical>
            <Stack.Item fill>
              <TextFieldControl
                label={t('administration:users.create.user.labels.email')}
                info={t('administration:users.create.user.labels.email.info')}
                name="email"
                type="email"
                required
                autoFocus
                autoComplete="off"
              />
            </Stack.Item>
            <Stack.Item fill>
              <SelectControl
                label={t('administration:users.create.user.labels.language')}
                name="language"
                options={[{
                  id: 'inviting-user-lang-field',
                  items: config.get('AVAILABLE_LANGUAGES'),
                }]}
              />
            </Stack.Item>
          </Stack>
        </ModalView>
      </Form>
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  users: 'UsersCrate.users',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
  config: 'config',
})(
  withTranslation(['administration', 'profile', 'platform'])(errorBoundary(InviteUser)),
));
